import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@resource-kit/ui/react';

export interface DispatchConfirmDialogProps {
  open: boolean;
  /** `formatBatchDay` of the date captured when the dialog opened. */
  batchLabel: string;
  /** Delivery jobs the run sends. */
  pendReady: number;
  /** Driver tasks the run creates. */
  tasks: number;
  /** Pre-check anomalies still on the batch. */
  anomalies: number;
  /** The dry-run is refetching: the counts may change, so the run waits. */
  refreshing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function Sum({ testId, label, value }: { testId: string; label: string; value: number }) {
  return (
    <div data-testid={testId} className="rounded-lg bg-muted px-3 py-2.5">
      <div className="text-[12.5px] text-muted-foreground">{label}</div>
      <div className="text-[22px] font-display font-bold text-foreground">{value}</div>
    </div>
  );
}

/**
 * The run confirmation (#2475, design `.dlg`): batch + count, three numbers,
 * and what the run will do. Anomalies do not disable the run (the backend
 * stops at the first failing group); the note says so instead.
 */
export function DispatchConfirmDialog({
  open,
  batchLabel,
  pendReady,
  tasks,
  anomalies,
  refreshing,
  onCancel,
  onConfirm,
}: DispatchConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      className="max-w-[500px] rounded-2xl border border-border bg-surface-1"
    >
      <div data-testid="dispatch-run-confirm-dialog">
        <div className="px-[22px] pb-1 pt-5 text-[17px] font-semibold">
          {t('dispatch.runCard.confirmTitle', { batch: batchLabel, count: pendReady })}
        </div>
        <div className="flex flex-col gap-3.5 px-[22px] pb-[18px] pt-2 text-muted-foreground">
          <div className="grid grid-cols-3 gap-2">
            <Sum
              testId="dispatch-run-confirm-jobs"
              label={t('dispatch.runCard.confirmJobs')}
              value={pendReady}
            />
            <Sum
              testId="dispatch-run-confirm-tasks"
              label={t('dispatch.runCard.confirmTasks')}
              value={tasks}
            />
            <Sum
              testId="dispatch-run-confirm-anomalies"
              label={t('dispatch.runCard.confirmAnomalies')}
              value={anomalies}
            />
          </div>
          <div>
            {anomalies > 0
              ? t('dispatch.runCard.confirmNoteAnomalies', { count: anomalies })
              : t('dispatch.runCard.confirmNote')}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-[22px] py-3.5">
          <Button variant="outline" onClick={onCancel}>
            {t('ui.cancel')}
          </Button>
          <Button
            data-testid="dispatch-run-confirm"
            onClick={onConfirm}
            disabled={refreshing || pendReady === 0}
          >
            {t('dispatch.runCard.confirmRun')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
